import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { TokenService } from './token.service';
import { environment } from '@environment';

/**
 * Environment configuration placeholders.
 * @constant {string} CORE_API_BASE_URL - The base endpoint for the Liife Core API.
 * @constant {string} PUBLIC_KEY - The public key used for x-public-key headers in requests.
 */
const CORE_API_BASE_URL = environment.coreApiBaseUrl;
const PUBLIC_KEY = environment.adminTenantPublicKey;

/**
 * UserService
 * * Manages all HTTP interactions related to User entities.
 * Handles fetching, creating, and registering users, as well as error handling for 401 states.
 */
@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private router = inject(Router);

  /**
   * Fetches a list of users from the API.
   * Applies a default filter to exclude deleted users and include their applications.
   * * @returns {Observable<any>} An observable containing the array of user objects.
   */
  getUsers(): Observable<any> {
    const url = `${CORE_API_BASE_URL}/users`;
    const token = this.tokenService.getToken();

    const headers = new HttpHeaders({
      'Accept': 'application/json, text/plain, */*',
      'Authorization': `Bearer ${token}`,
      'x-public-key': PUBLIC_KEY,
      'x-token-ready': 'true'
    });

    // Define standard filters for the user list query
    const params = {
      filter: JSON.stringify({
        where: { isDeleted: false },
        include: ['applications']
      })
    };

    return this.http.get(url, { headers, params }).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Fetches a single user by their unique ID.
   * * @param {string} userId - The unique identifier of the user to retrieve.
   * @returns {Observable<any>} An observable containing the user details.
   */
  getUserById(userId: string): Observable<any> {
    const filter = {
      include: ['applications']
    };
    const url = `${CORE_API_BASE_URL}/users/${userId}?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const token = this.tokenService.getToken();

    const headers = new HttpHeaders({
      'Accept': 'application/json, text/plain, */*',
      'Authorization': `Bearer ${token}`,
      'x-public-key': PUBLIC_KEY,
      'x-token-ready': 'true'
    });

    return this.http.get(url, { headers }).pipe(
      catchError((error) => this.handleError(error))
    );
  }

  /**
   * Creates a new user within the specific tenant context.
   * * @param {any} payload - The user data object containing fields like firstName, email, phone, etc.
   * @returns {Observable<any>} An observable of the creation response.
   */
  createUser(payload: any): Observable<any> {
    const url = `${CORE_API_BASE_URL}/tenants/users`;
    const token = this.tokenService.getToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'x-public-key': PUBLIC_KEY,
      'x-token-ready': 'true'
    });

    return this.http.post(url, payload, { headers });
  }

  /**
   * Registers a new user via the auth service.
   * Automatically generates a random password if one is not provided.
   * * @param {any} payload - The registration details.
   * @returns {Observable<any>} An observable of the registration result.
   */
  registerUser(payload: any): Observable<any> {
    const url = `${CORE_API_BASE_URL}/auth/register`;
    const token = this.tokenService.getToken();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${token}`,
      'x-public-key': PUBLIC_KEY
    });

    // Ensure a password exists for the registration payload
    const finalPayload = {
      ...payload,
      password: payload.password || this.generateRandomPassword(),
    };

    return this.http.post(url, finalPayload, { headers });
  }

  /**
   * Helper method to generate a random 12-character string.
   * Used for setting temporary passwords during user creation.
   * * @returns {string} A random string containing alphanumeric and special characters.
   */
  private generateRandomPassword(): string {
    const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  /**
   * Standard error handler for HTTP requests.
   * Specifically watches for 401 Unauthorized errors to trigger a logout/redirect.
   * * @param {any} error - The error object returned from the HTTP Client.
   * @returns {Observable<never>} Throws the error to be handled by the subscriber.
   */
  private handleError(error: any) {
    if (error.status === 401) {
      this.tokenService.clearToken();
      this.router.navigate(['/login']);
    }
    return throwError(() => error);
  }
}